import type { CSSProperties, MouseEvent, RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ButtonBordersWidth,
  ButtonColors,
  ButtonCorners,
  ButtonIconSizes,
  IconPositions,
} from 'atoms/Button/Button.h'
import { Icon, IconNames, IconSizes } from 'atoms/Icon'
import { TextBoxMaxLine1FontSmBold } from 'atoms/TextBox/Templates'
import { COLOR } from 'vars/color'
import {
  ContentActionMenuItemSC,
  ContentActionMenuListSC,
  ContentActionMenuLinkSC,
  ContentActionMenuRootSC,
  ContentActionMenuTriggerSC,
} from './ContentActionMenu.styled'
import { useContentActionMenuShift } from './useContentActionMenuShift'

export type ContentActionMenuItem = {
  id: string
  label: string
  icon: IconNames
  href?: string
  onSelect?: () => void
}

export type ContentActionMenuAnchorRef = RefObject<HTMLElement | null>

type Props = {
  /** Card/container ref for click-outside dismiss; not used for menu positioning */
  anchorRef: ContentActionMenuAnchorRef
  items: ContentActionMenuItem[]
  ariaLabel: string
  menuOffset?: number
  onOpen?: () => void
  onDismiss?: () => void
}

export const ContentActionMenu = ({
  anchorRef,
  items,
  ariaLabel,
  menuOffset,
  onOpen,
  onDismiss,
}: Props) => {
  const [opened, setOpened] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const menuPosition = useContentActionMenuShift({
    menuOffset,
    opened,
    listRef,
    triggerRef,
  })

  useEffect(() => {
    if (!opened) return

    const anchorElement = anchorRef.current

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const inRoot = rootRef.current?.contains(target)
      const inAnchor = anchorElement?.contains(target)
      const inMenu = listRef.current?.contains(target)

      if (!inRoot && !inAnchor && !inMenu) {
        onDismiss?.()
        setOpened(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss?.()
        setOpened(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [anchorRef, onDismiss, opened])

  if (!items.length) return null

  const stopCardNavigation = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleTriggerClick = (event: MouseEvent) => {
    stopCardNavigation(event)
    setOpened((value) => {
      if (value) {
        onDismiss?.()
        return false
      }

      onOpen?.()
      return true
    })
  }

  const handleItemClick =
    (item: ContentActionMenuItem) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation()

      if (!item.href) {
        event.preventDefault()
      }

      item.onSelect?.()
      setOpened(false)
    }

  const menuList = opened ? (
    <ContentActionMenuListSC
      ref={listRef}
      role='menu'
      onClick={(event) => event.stopPropagation()}
      style={
        {
          left: `${menuPosition.left}px`,
          top: `${menuPosition.top}px`,
        } as CSSProperties
      }
    >
      {items.map((item) => {
        const content = (
          <>
            <Icon
              name={item.icon}
              size={IconSizes.xSm}
              color={COLOR.TEXT_ICON_PRIMARY}
            />
            <TextBoxMaxLine1FontSmBold title={item.label} width='100' />
          </>
        )

        return item.href ? (
          <ContentActionMenuLinkSC
            key={item.id}
            href={item.href}
            role='menuitem'
            onClick={handleItemClick(item)}
          >
            {content}
          </ContentActionMenuLinkSC>
        ) : (
          <ContentActionMenuItemSC
            key={item.id}
            type='button'
            role='menuitem'
            onClick={handleItemClick(item)}
          >
            {content}
          </ContentActionMenuItemSC>
        )
      })}
    </ContentActionMenuListSC>
  ) : null

  return (
    <ContentActionMenuRootSC
      ref={rootRef}
      onClick={(event) => event.stopPropagation()}
    >
      <ContentActionMenuTriggerSC
        ref={triggerRef}
        type='button'
        icon={IconNames.dots}
        iconPosition={IconPositions.Center}
        iconSize={ButtonIconSizes.mSm}
        corners={ButtonCorners.CORNERS_ROUND}
        borderWidth={ButtonBordersWidth.BORDER_0}
        colorDefault={ButtonColors.COLOR_TEXT_SECONDARY}
        ariaLabel={ariaLabel}
        ariaExpanded={opened}
        onClick={handleTriggerClick}
      />
      {opened && typeof document !== 'undefined'
        ? createPortal(menuList, document.body)
        : null}
    </ContentActionMenuRootSC>
  )
}
